import { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { useSaveContent } from "./AdminLayout";
import { Card, Text, Area, Lines, ImageField, ObjectList, btnPrimary, btnGhost } from "./fields";

export default function AdminDiensten() {
  const { services } = useContent();
  const { save, saving } = useSaveContent();
  const [active, setActive] = useState(0);
  const [draft, setDraft] = useState(services[0]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(services[active]);
    setDirty(false);
  }, [active, services]);

  const set = (key) => (v) => {
    setDraft((d) => ({ ...d, [key]: v }));
    setDirty(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const list = services.map((s, i) => (i === active ? { ...draft, slug: s.slug } : s));
    await save({ services: list });
  };

  if (!draft) return null;

  return (
    <div data-testid="admin-diensten-page" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold uppercase tracking-wide text-[#111111]">Diensten</h1>
        <p className="mt-1 text-sm text-[#555555]">Pas de teksten, punten en veelgestelde vragen per dienst aan.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {services.map((s, i) => (
          <button
            key={s.slug}
            type="button"
            data-testid={`service-tab-${s.slug}`}
            onClick={() => {
              if (dirty && !window.confirm("Niet-opgeslagen wijzigingen gaan verloren. Doorgaan?")) return;
              setActive(i);
            }}
            className={`rounded-[4px] border px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-wide ${
              i === active ? "border-[#111111] bg-[#111111] text-white" : "border-[#D8D6D0] bg-white text-[#555555]"
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="service-editor">
        <Card title={`Dienst: ${draft.title}`}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Text label="Naam" value={draft.title} onChange={set("title")} testid="service-title" />
            <Text label="URL" value={`/diensten/${draft.slug}`} onChange={() => {}} />
          </div>
          <Area label="Korte omschrijving (kaartje op homepage)" value={draft.short} onChange={set("short")} rows={2} testid="service-short" />
          <Area label="Introductietekst" value={draft.intro} onChange={set("intro")} rows={4} testid="service-intro" />
          <Lines label="Werkzaamheden (punten)" value={draft.points} onChange={set("points")} testid="service-points" />
          <ImageField label="Foto" value={draft.image} onChange={set("image")} testid="service-image-upload" />
        </Card>
        <Card title="Tekstblokken">
          <ObjectList
            label="Blokken"
            value={draft.sections}
            onChange={set("sections")}
            testid="service-sections"
            empty={{ heading: "", text: "" }}
            fields={[{ key: "heading", label: "Kop" }, { key: "text", label: "Tekst", multiline: true }]}
          />
        </Card>
        <Card title="Veelgestelde vragen">
          <ObjectList
            label="Vragen"
            value={draft.faq || []}
            onChange={(v) => set("faq")(v.length ? v : undefined)}
            testid="service-faq"
            empty={{ q: "", a: "" }}
            fields={[{ key: "q", label: "Vraag" }, { key: "a", label: "Antwoord", multiline: true }]}
          />
        </Card>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={() => { setDraft(services[active]); setDirty(false); }} className={btnGhost}><RotateCcw className="h-4 w-4" /> Herstel</button>
          <button type="submit" data-testid="service-save" disabled={saving || !dirty} className={btnPrimary}><Save className="h-4 w-4" /> {saving ? "Opslaan..." : "Dienst opslaan"}</button>
        </div>
      </form>
    </div>
  );
}
